import { useMemo } from "react";
import { Transaction, useFinance } from "@/context/FinanceContext";
import { OFXTransaction } from "@/lib/ofxParser";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface MatchedRow {
  ofx: OFXTransaction;
  matches: Transaction[]; // candidatos
  selectedMatchId: string | null;
  selected: boolean; // marcado para conciliar
  alreadyReconciled?: Transaction; // se fitid já existe
}

export interface UnmatchedRow {
  ofx: OFXTransaction;
}

export interface MatchResult {
  matched: MatchedRow[];
  unmatched: UnmatchedRow[];
  alreadyImported: MatchedRow[];
}

const MATCH_WINDOW_DAYS = 3;

function daysBetween(a: string, b: string): number {
  if (!a || !b) return Infinity;
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const da = new Date(ay, am - 1, ad).getTime();
  const db = new Date(by, bm - 1, bd).getTime();
  return Math.abs(Math.round((da - db) / 86400000));
}

export function matchOFXAgainstTransactions(
  ofxList: OFXTransaction[],
  transactions: Transaction[],
  accountId: string,
): MatchResult {
  const matched: MatchedRow[] = [];
  const unmatched: UnmatchedRow[] = [];
  const alreadyImported: MatchedRow[] = [];

  // Index existing fitids
  const fitidIndex = new Map<string, Transaction>();
  for (const t of transactions) {
    if (t.ofxFitid && t.accountId === accountId) {
      fitidIndex.set(t.ofxFitid, t);
    }
  }

  // Eligible transactions on same account, not yet reconciled, kind normal or transfer
  const candidates = transactions.filter(
    (t) => t.accountId === accountId && t.reconciled !== true,
  );

  for (const ofx of ofxList) {
    // Already imported?
    const existing = fitidIndex.get(ofx.fitid);
    if (existing) {
      alreadyImported.push({
        ofx,
        matches: [existing],
        selectedMatchId: existing.id,
        selected: false,
        alreadyReconciled: existing,
      });
      continue;
    }

    const ofxIsCredit = ofx.amount > 0; // entrada
    const target = Math.abs(ofx.amount);

    const found = candidates.filter((t) => {
      const sameSign = ofxIsCredit ? t.type === "receita" : t.type === "despesa";
      if (!sameSign) return false;
      if (Math.abs(t.value - target) > 0.005) return false;
      const dist = daysBetween(t.dueDate || t.issueDate || "", ofx.date);
      return dist <= MATCH_WINDOW_DAYS;
    });

    if (found.length === 0) {
      unmatched.push({ ofx });
    } else {
      // Pick closest by date as default
      const sorted = [...found].sort((a, b) => {
        const da = daysBetween(a.dueDate || a.issueDate || "", ofx.date);
        const db = daysBetween(b.dueDate || b.issueDate || "", ofx.date);
        return da - db;
      });
      matched.push({
        ofx,
        matches: sorted,
        selectedMatchId: sorted[0].id,
        selected: true,
      });
    }
  }

  return { matched, unmatched, alreadyImported };
}

export function useOFXReconciliation() {
  const { transactions } = useFinance();
  const { user } = useAuth();

  const reconcileMany = async (
    rows: { transactionId: string; ofx: OFXTransaction }[],
    importId: string | null,
  ): Promise<{ ok: number; fail: number; errors: string[] }> => {
    let ok = 0;
    let fail = 0;
    const errors: string[] = [];
    for (const r of rows) {
      try {
        const { error } = await supabase
          .from("transactions")
          .update({
            settled: true,
            payment_date: r.ofx.date,
            reconciled: true,
            reconciled_at: new Date().toISOString(),
            ofx_fitid: r.ofx.fitid,
            ofx_import_id: importId,
          } as any)
          .eq("id", r.transactionId);
        if (error) {
          fail++;
          errors.push(error.message);
        } else {
          ok++;
        }
      } catch (e: any) {
        fail++;
        errors.push(e?.message || String(e));
      }
    }
    return { ok, fail, errors };
  };

  const createImportRecord = async (data: {
    accountId: string;
    fileName: string;
    bankId?: string;
    periodStart?: string;
    periodEnd?: string;
    totalTransactions: number;
    matchedCount: number;
    createdCount: number;
    skippedCount: number;
  }): Promise<string | null> => {
    if (!user) return null;
    const { data: row, error } = await supabase
      .from("ofx_imports" as any)
      .insert({
        user_id: user.id,
        account_id: data.accountId,
        file_name: data.fileName,
        bank_id: data.bankId || null,
        period_start: data.periodStart || null,
        period_end: data.periodEnd || null,
        total_transactions: data.totalTransactions,
        matched_count: data.matchedCount,
        created_count: data.createdCount,
        skipped_count: data.skippedCount,
      })
      .select("id")
      .single();
    if (error) {
      console.error("Erro ao registrar importação OFX:", error);
      return null;
    }
    return (row as any)?.id ?? null;
  };

  const undoImport = async (importId: string): Promise<boolean> => {
    const { error } = await supabase
      .from("transactions")
      .update({
        reconciled: false,
        reconciled_at: null,
        ofx_fitid: null,
        ofx_import_id: null,
      } as any)
      .eq("ofx_import_id", importId);
    if (error) {
      console.error("Erro ao desfazer importação:", error);
      return false;
    }
    return true;
  };

  return {
    reconcileMany,
    createImportRecord,
    undoImport,
    transactions,
  };
}
